import { S3Client, PutObjectCommand, DeleteObjectCommand } from "@aws-sdk/client-s3";

// Initialize S3 client with credentials from environment variables
const getS3Client = () => {
  // Check multiple possible variable names for flexibility
  const accessKeyId = 
    import.meta.env.VITE_AWS_ACCESS_KEY_ID ||
    import.meta.env.AWS_ACCESS_KEY_ID;
  const secretAccessKey = 
    import.meta.env.VITE_AWS_SECRET_ACCESS_KEY ||
    import.meta.env.AWS_SECRET_ACCESS_KEY;
  const region = 
    import.meta.env.VITE_AWS_REGION || 
    import.meta.env.VITE_AWS_DEFAULT_REGION ||
    import.meta.env.AWS_REGION ||
    import.meta.env.AWS_DEFAULT_REGION ||
    "us-east-1";

  if (!accessKeyId || !secretAccessKey) {
    console.error("Missing AWS credentials. Available env vars:", {
      VITE_AWS_ACCESS_KEY_ID: import.meta.env.VITE_AWS_ACCESS_KEY_ID ? "***" : undefined,
      VITE_AWS_SECRET_ACCESS_KEY: import.meta.env.VITE_AWS_SECRET_ACCESS_KEY ? "***" : undefined,
    });
    throw new Error("AWS credentials not configured. Please set VITE_AWS_ACCESS_KEY_ID and VITE_AWS_SECRET_ACCESS_KEY in .env file");
  }

  return new S3Client({
    region: region,
    credentials: {
      accessKeyId: accessKeyId,
      secretAccessKey: secretAccessKey,
    },
  });
};

/**
 * Upload file directly to S3 bucket
 * @param file - File to upload
 * @param s3Key - S3 key (path) where file should be stored
 * @param bucketName - S3 bucket name
 * @returns Public URL of the uploaded file
 */
export const uploadFileToS3 = async (
  file: File,
  s3Key: string,
  bucketName: string
): Promise<string> => {
  const s3Client = getS3Client();

  // Convert file to ArrayBuffer (browser-compatible)
  const arrayBuffer = await file.arrayBuffer();

  // Create PutObject command
  // Note: In browser, we can pass ArrayBuffer directly, but for better compatibility,
  // we'll convert it to Uint8Array
  const uint8Array = new Uint8Array(arrayBuffer);

  const command = new PutObjectCommand({
    Bucket: bucketName,
    Key: s3Key,
    Body: uint8Array,
    ContentType: file.type,
    // Make the object publicly readable
    ACL: "public-read",
  });

  try {
    // Upload to S3
    await s3Client.send(command);

    // Construct public URL
    const region = import.meta.env.VITE_AWS_REGION || import.meta.env.VITE_AWS_DEFAULT_REGION || "us-east-1";
    const publicUrl = `https://${bucketName}.s3.${region}.amazonaws.com/${s3Key}`;

    return publicUrl;
  } catch (error: any) {
    console.error("S3 upload error:", error);
    throw new Error(`Failed to upload file to S3: ${error.message}`);
  }
};

/**
 * Generate S3 key for category image
 * Format: categories/{categoryId}/imagefile.{ext}
 */
export const generateCategoryImageKey = (categoryId: number, fileName: string): string => {
  const fileExtension = fileName.split(".").pop() || "jpg";
  return `categories/${categoryId}/imagefile.${fileExtension}`;
};

/**
 * Generate S3 key for collection image
 * Format: collections/{collectionId}/imagefile.{ext}
 */
export const generateCollectionImageKey = (collectionId: number, fileName: string): string => {
  const fileExtension = fileName.split(".").pop() || "jpg";
  return `collections/${collectionId}/imagefile.${fileExtension}`;
};

/**
 * Generate S3 key for product image
 * Format: products/{productId}/image_{timestamp}.{ext}
 */
export const generateProductImageKey = (productId: number, fileName: string): string => {
  const fileExtension = fileName.split(".").pop() || "jpg";
  const timestamp = Date.now();
  const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, "_").toLowerCase();
  const nameWithoutExt = sanitizedFileName.replace(/\.[^/.]+$/, "");
  return `products/${productId}/${nameWithoutExt}_${timestamp}.${fileExtension}`;
};

/**
 * Generate S3 key for hero featured collection image
 * Format: hero-featured-collections/{collectionId}/imagefile.{ext}
 */
export const generateHeroFeaturedCollectionImageKey = (collectionId: number, fileName: string): string => {
  const fileExtension = fileName.split(".").pop() || "jpg";
  return `hero-featured-collections/${collectionId}/imagefile.${fileExtension}`;
};

/**
 * Generate S3 key for purchase invoice
 * Format: purchases/{purchaseId}/invoice_{timestamp}.{ext}
 */
export const generatePurchaseInvoiceKey = (purchaseId: number, fileName: string): string => {
  const fileExtension = fileName.split(".").pop() || "pdf";
  const timestamp = Date.now();
  const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, "_").toLowerCase();
  const nameWithoutExt = sanitizedFileName.replace(/\.[^/.]+$/, "");
  return `purchases/${purchaseId}/invoice_${nameWithoutExt}_${timestamp}.${fileExtension}`;
};

/**
 * Extract S3 key from a public S3 URL
 * @param url - Public S3 URL (e.g., https://bucket.s3.region.amazonaws.com/key)
 * @returns S3 key or null if URL is not a valid S3 URL
 */
export const extractS3KeyFromUrl = (url: string): string | null => {
  try {
    // Match S3 URL pattern: https://bucket.s3.region.amazonaws.com/key
    const s3UrlPattern = /https?:\/\/[^\/]+\.s3[^\/]*\.amazonaws\.com\/(.+)/;
    const match = url.match(s3UrlPattern);
    if (match && match[1]) {
      return decodeURIComponent(match[1]);
    }
    return null;
  } catch (error) {
    console.error("Error extracting S3 key from URL:", error);
    return null;
  }
};

/**
 * Get S3 bucket name from environment variables
 * @returns S3 bucket name
 * @throws Error if bucket name is not configured
 */
export const getS3BucketName = (): string => {
  // Check multiple possible variable names for flexibility
  const bucketName = 
    import.meta.env.VITE_AWS_S3_BUCKET || 
    import.meta.env.VITE_S3_BUCKET_NAME ||
    import.meta.env.VITE_AWS_BUCKET ||
    import.meta.env.VITE_S3_BUCKET;
  
  if (!bucketName) {
    // Log all environment variables that start with VITE_ for debugging
    const allEnvVars = Object.keys(import.meta.env)
      .filter(key => key.startsWith('VITE_'))
      .reduce((acc, key) => {
        acc[key] = import.meta.env[key] ? (key.includes('KEY') || key.includes('SECRET') ? '***' : import.meta.env[key]) : undefined;
        return acc;
      }, {} as Record<string, string | undefined>);
    
    console.error("S3 bucket name not configured. Available VITE_ env vars:", allEnvVars);
    console.error("This error typically means:");
    console.error("1. VITE_AWS_S3_BUCKET was not set in GitHub Actions secrets, OR");
    console.error("2. The build was done before the secret was added, OR");
    console.error("3. The secret value is empty. Please check GitHub repository settings.");
    throw new Error("S3 bucket name not configured. Please set VITE_AWS_S3_BUCKET in GitHub Actions secrets and redeploy.");
  }
  
  return bucketName;
};

/**
 * Delete file from S3 bucket
 * @param s3Key - S3 key (path) of the file to delete
 * @param bucketName - S3 bucket name
 */
export const deleteFileFromS3 = async (
  s3Key: string,
  bucketName: string
): Promise<void> => {
  const s3Client = getS3Client();

  const command = new DeleteObjectCommand({
    Bucket: bucketName,
    Key: s3Key,
  });

  try {
    await s3Client.send(command);
  } catch (error: any) {
    console.error("S3 delete error:", error);
    throw new Error(`Failed to delete file from S3: ${error.message}`);
  }
};

